import React, { useContext, useRef, useState } from "react";
import { Avatar, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, LinearProgress, Paper, TextField, Tooltip, Typography } from "@material-ui/core";
import { Add, ChevronLeft, ChevronRight, Close, DeleteOutline, EditOutlined, ErrorOutline, PeopleOutline, Refresh, Search } from "@material-ui/icons";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import UserModal from "../../components/UserModal";
import ForbiddenPage from "../../components/ForbiddenPage";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { getBackendUrl } from "../../config";
import ContactsTheme from "../Contacts/theme";
import useUsersList from "./useUsersList";
import useStyles from "./styles";

const userInitials = name => String(name || "U").trim().split(/\s+/).slice(0, 2).map(part => part[0]).join("").toUpperCase();

function UsersContent() {
  const classes = useStyles();
  const { user: account, socket } = useContext(AuthContext);
  const list = useUsersList({ companyId: account.companyId, socket });
  const [editor, setEditor] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);
  const deleteLock = useRef(false);
  const first = list.users.length ? (list.pageNumber - 1) * 20 + 1 : 0;
  const closeDelete = () => { if (!deleteLock.current) setDeleting(null); };
  const deleteUser = async () => {
    if (!deleting || deleteLock.current) return;
    deleteLock.current = true; setBusy(true);
    try {
      await api.delete(`/users/${deleting.id}`);
      toast.success("Usuário excluído."); setDeleting(null); list.refresh();
    } catch (err) { toastError(err); }
    finally { deleteLock.current = false; setBusy(false); }
  };
  return (
    <section className={classes.root} aria-label="Gerenciar usuários">
      <header className={classes.header}>
        <div className={classes.heading}>
          <span className={classes.headingIcon}><PeopleOutline /></span>
          <Typography component="h1" className={classes.title}>Usuários</Typography>
          <Chip size="small" label={list.count} className={classes.count} />
        </div>
        <Button color="primary" variant="contained" startIcon={<Add />} className={classes.add} onClick={() => setEditor({})}>Novo usuário</Button>
      </header>
      <Paper variant="outlined" className={classes.panel}>
        <div className={classes.toolbar}>
          <TextField type="search" placeholder="Buscar por nome ou e-mail" variant="outlined" size="small" className={classes.search}
            value={list.search} onChange={event => list.setSearch(event.target.value)} inputProps={{ "aria-label": "Buscar usuário" }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
          <Tooltip title="Atualizar usuários"><span><IconButton aria-label="Atualizar usuários" disabled={list.loading} onClick={list.refresh} className={classes.action}><Refresh /></IconButton></span></Tooltip>
        </div>
        <div className={classes.progress}>{list.loading && <LinearProgress />}</div>
        {list.error && <div role="alert" className={classes.error}><ErrorOutline /><span>Não foi possível carregar os usuários.</span><Button onClick={list.refresh}>Tentar novamente</Button></div>}
        <div className={classes.columns} aria-hidden="true"><span>Usuário</span><span>Status</span><span>Perfil</span><span>Horário de trabalho</span><span>Ações</span></div>
        <div className={classes.rows} aria-label="Lista de usuários" role="region" aria-busy={list.loading}>
          {list.users.map(person => (
            <article key={person.id} aria-label={person.name} className={classes.row}>
              <div className={classes.identity}>
                <Avatar className={classes.avatar} alt={person.name} src={person.profileImage ? `${getBackendUrl()}/public/company${person.companyId}/user/${person.profileImage}` : undefined}>{userInitials(person.name)}</Avatar>
                <div className={classes.identityText}>
                  <div className={classes.nameLine}><Tooltip title={person.name}><span className={classes.name}>{person.name}</span></Tooltip>{Number(person.id) === Number(account.id) && <span className={classes.you}>Você</span>}</div>
                  <Tooltip title={person.email}><span className={classes.email}>{person.email}</span></Tooltip>
                  <span className={classes.userId}>#{person.id}</span>
                </div>
              </div>
              <span className={classes.status}><span className={`${classes.statusDot} ${person.online ? classes.online : ""}`} />{person.online ? "Online" : "Offline"}</span>
              <span className={`${classes.profile} ${person.profile === "admin" ? classes.admin : ""}`}>{person.profile === "admin" ? "Administrador" : "Atendente"}</span>
              <span className={classes.schedule}>{person.startWork && person.endWork ? `${person.startWork.slice(0, 5)} – ${person.endWork.slice(0, 5)}` : "Não definido"}</span>
              <div className={classes.actions}>
                <Tooltip title="Editar usuário"><IconButton className={classes.action} aria-label={`Editar ${person.name}`} onClick={() => setEditor(person)}><EditOutlined fontSize="small" /></IconButton></Tooltip>
                <Tooltip title={Number(person.id) === Number(account.id) ? "Sua conta está em uso" : "Excluir usuário"}><span><IconButton className={`${classes.action} ${classes.delete}`} disabled={Number(person.id) === Number(account.id)} aria-label={`Excluir ${person.name}`} onClick={() => setDeleting(person)}><DeleteOutline fontSize="small" /></IconButton></span></Tooltip>
              </div>
            </article>
          ))}
          {!list.users.length && <div className={classes.empty}>
            {list.loading ? <CircularProgress size={28} aria-label="Carregando usuários" /> : !list.error && <><PeopleOutline /><Typography>{list.searchParam ? "Nenhum usuário encontrado" : "Nenhum usuário cadastrado"}</Typography><Button color="primary" onClick={() => list.searchParam ? list.setSearch("") : setEditor({})}>{list.searchParam ? "Limpar busca" : "Novo usuário"}</Button></>}
          </div>}
        </div>
        <footer className={classes.footer}>
          <span aria-live="polite">{first}–{first ? first + list.users.length - 1 : 0} de {list.count}<span className={classes.pageSize}> · 20 por página</span></span>
          <div className={classes.pagination}>
            <IconButton className={classes.action} aria-label="Página anterior" disabled={list.loading || list.pageNumber === 1} onClick={() => list.setPage(list.pageNumber - 1)}><ChevronLeft /></IconButton>
            <span>Página {list.pageNumber}</span>
            <IconButton className={classes.action} aria-label="Próxima página" disabled={list.loading || !list.hasMore} onClick={() => list.setPage(list.pageNumber + 1)}><ChevronRight /></IconButton>
          </div>
        </footer>
      </Paper>
      {editor && <UserModal open userId={editor.id} onClose={() => setEditor(null)} onSaved={list.refresh} />}
      <Dialog open={Boolean(deleting)} onClose={closeDelete} maxWidth="xs" fullWidth aria-labelledby="delete-user-title">
        <DialogTitle id="delete-user-title">Excluir usuário?<IconButton className={classes.dialogClose} disabled={busy} onClick={closeDelete} aria-label="Fechar confirmação"><Close /></IconButton></DialogTitle>
        <DialogContent><Typography className={classes.deleteName}>{deleting?.name}</Typography><Typography color="textSecondary">Este usuário perderá o acesso ao sistema. Essa ação não pode ser desfeita.</Typography></DialogContent>
        <DialogActions><Button variant="outlined" disabled={busy} onClick={closeDelete}>Cancelar</Button><Button variant="contained" color="primary" disabled={busy} onClick={deleteUser}>{busy ? "Excluindo…" : "Excluir usuário"}</Button></DialogActions>
      </Dialog>
    </section>
  );
}

export default function Users() {
  const { user } = useContext(AuthContext);
  return <ContactsTheme>{user?.profile === "admin" ? <UsersContent /> : <ForbiddenPage />}</ContactsTheme>;
}
